// Todo create form. Title, details, Visibility (Team-visible /
// Private), Assign-to (Individual / Team), Rock + Milestone cascade,
// Due + Priority pills, inline subtasks, labels. Replicates the
// Drillcut TodoForm structure exactly, theme-swapped.

import { useState } from "react";
import { EyeOff, Info, Lock, Plus, UserRound, Users, X } from "lucide-react";
import type { CreateSeed } from "./types";
import {
  FormSection,
  FormLabel,
  Hint,
  DC,
  ENTITY,
  twoCol,
  iconBtn,
  inlineInput,
  ghostAdd,
} from "./primitives";
import { DcInput, TabChip, PrioritySelect, PillRemoveBtn } from "./fields";
import { RichTextEditor } from "../../components/RichTextEditor";
import { MilestoneSelect, PersonSelect, TeamSelect, RockSelect } from "./selects";
import { Footer } from "./Footer";
import { tpApi } from "../../api";
import {
  TODO_PRIORITY,
  TODO_STATUS,
} from "../../../../../../../shared/team-planner/schemas";

interface SubtaskDraft {
  id: string;
  title: string;
}

interface TodoFormProps {
  seed?: CreateSeed;
  onClose: () => void;
  onCreated: () => void;
}

function seedPriority(
  raw: string | undefined,
): (typeof TODO_PRIORITY)[number] | null {
  return raw && (TODO_PRIORITY as readonly string[]).includes(raw)
    ? (raw as (typeof TODO_PRIORITY)[number])
    : null;
}
function seedStatus(
  raw: string | undefined,
): (typeof TODO_STATUS)[number] | undefined {
  return raw && (TODO_STATUS as readonly string[]).includes(raw)
    ? (raw as (typeof TODO_STATUS)[number])
    : undefined;
}

function cryptoId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : String(Date.now() + Math.random());
}
function parseDue(raw: string): Date | null {
  const t = raw.trim();
  if (!t) return null;
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), 17, 0, 0));
}

export function TodoForm({ seed, onClose, onCreated }: TodoFormProps) {
  const [title, setTitle] = useState(seed?.title ?? "");
  const [details, setDetails] = useState("");
  const [privateTodo, setPrivateTodo] = useState(false);
  const [assignTo, setAssignTo] = useState<"person" | "team">("person");
  const [owner, setOwner] = useState<string | null>(null);
  const [team, setTeam] = useState<string | null>(null);
  const [rock, setRock] = useState<string | null>(seed?.rockId ?? null);
  const [milestone, setMilestone] = useState<string | null>(null);
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState<
    (typeof TODO_PRIORITY)[number] | null
  >(seedPriority(seed?.priority));
  const seededStatus = seedStatus(seed?.status);
  const [subtasks, setSubtasks] = useState<SubtaskDraft[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const canSubmit = title.trim().length > 0 && !pending;

  const summaryBits: string[] = [];
  if (rock) summaryBits.push("linked Rock");
  if (due) summaryBits.push(`due ${due}`);
  if (subtasks.length)
    summaryBits.push(`${subtasks.length} subtask${subtasks.length === 1 ? "" : "s"}`);
  const summary = summaryBits.length === 0 ? "Unlinked to-do" : summaryBits.join(" · ");

  async function submit() {
    setSubmitError(null);
    setPending(true);
    try {
      const created = await tpApi.createTodo({
        title: title.trim(),
        description:
          details.trim() === "<p></p>" || details.trim() === ""
            ? undefined
            : details.trim(),
        priority: priority ?? undefined,
        status: seededStatus,
        dueAt: parseDue(due)?.toISOString() ?? null,
        rockId: rock ?? undefined,
        milestoneId: rock ? (milestone ?? undefined) : undefined,
        labels: labels.length > 0 ? labels : undefined,
        ownerType: privateTodo
          ? "individual"
          : assignTo === "team"
            ? "team"
            : "individual",
        parentTeamId:
          !privateTodo && assignTo === "team" ? (team ?? undefined) : undefined,
        isPrivate: privateTodo,
      });
      // Re-point primary owner when the user picked someone other than
      // themselves in the Individual flow. Server seeds the creator on
      // insert.
      if (!privateTodo && assignTo === "person" && owner) {
        await tpApi.setPrimaryOwner("todo", created.id, {
          subjectType: "user",
          subjectId: owner,
        });
      }
      // When the to-do is owned by a team, swap the primary owner to
      // that team so the team chip on the card renders.
      if (!privateTodo && assignTo === "team" && team) {
        await tpApi.setPrimaryOwner("todo", created.id, {
          subjectType: "team",
          subjectId: team,
        });
      }
      for (const s of subtasks) {
        if (!s.title.trim()) continue;
        await tpApi.createSubtask(created.id, { title: s.title.trim() });
      }
      onCreated();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Unknown error.");
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <div style={scrollBody}>
        <FormSection>
          <FormLabel required>What needs doing?</FormLabel>
          <DcInput
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Draft the vendor MSA for legal review"
          />
        </FormSection>

        <FormSection>
          <FormLabel>Details</FormLabel>
          <RichTextEditor
            value={details}
            onChange={setDetails}
            placeholder="Context, acceptance criteria, links…"
            minRows={3}
          />
        </FormSection>

        <FormSection>
          <FormLabel>Visibility</FormLabel>
          <div style={{ display: "flex", gap: 6, marginBottom: privateTodo ? 10 : 0 }}>
            <TabChip
              active={!privateTodo}
              onClick={() => setPrivateTodo(false)}
              icon={<Users size={13} />}
            >
              Team-visible
            </TabChip>
            <TabChip
              active={privateTodo}
              onClick={() => setPrivateTodo(true)}
              icon={<Lock size={13} />}
            >
              Private
            </TabChip>
          </div>
          {privateTodo && (
            <div style={privacyNote}>
              <EyeOff size={14} color={DC.grey} style={{ marginTop: 1 }} />
              <div>
                Only you will see this to-do. It won't appear on team boards, in
                Health counts, or in anyone's Day view.
              </div>
            </div>
          )}
        </FormSection>

        <FormSection>
          <FormLabel required>Assign to</FormLabel>
          {privateTodo ? (
            <div style={privateAssign}>
              Private to-dos are always self-assigned.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                <TabChip
                  active={assignTo === "person"}
                  onClick={() => setAssignTo("person")}
                  icon={<UserRound size={13} />}
                >
                  Individual
                </TabChip>
                <TabChip
                  active={assignTo === "team"}
                  onClick={() => setAssignTo("team")}
                  icon={<Users size={13} />}
                >
                  Team
                </TabChip>
              </div>
              {assignTo === "person" ? (
                <PersonSelect value={owner} onChange={setOwner} />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <TeamSelect value={team} onChange={setTeam} />
                  <div style={teamNote}>
                    <Info size={13} />
                    Any team member can pick it up. Point-of-contact is the team lead.
                  </div>
                </div>
              )}
            </>
          )}
        </FormSection>

        <div style={twoCol}>
          <FormSection>
            <FormLabel>Link to Rock</FormLabel>
            <RockSelect
              value={rock}
              onChange={(id) => {
                setRock(id);
                if (id !== rock) setMilestone(null);
              }}
            />
          </FormSection>
          <FormSection>
            <FormLabel>Milestone</FormLabel>
            <MilestoneSelect value={milestone} onChange={setMilestone} rockId={rock} />
          </FormSection>
        </div>

        <div style={twoCol}>
          <FormSection>
            <FormLabel>Due</FormLabel>
            <DcInput
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </FormSection>
          <FormSection>
            <FormLabel>Priority</FormLabel>
            <PrioritySelect value={priority} onChange={setPriority} />
          </FormSection>
        </div>

        <FormSection>
          <FormLabel>Subtasks</FormLabel>
          {subtasks.length === 0 ? (
            <Hint style={{ marginBottom: 8 }}>
              Optional — break the job into smaller chunks if it helps.
            </Hint>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
              {subtasks.map((s, i) => (
                <div
                  key={s.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 28px",
                    gap: 8,
                    alignItems: "center",
                    padding: "6px 8px 6px 10px",
                    background: DC.surface,
                    border: `1.5px solid ${DC.border}`,
                    borderLeft: `3px solid ${ENTITY.subtask.color}`,
                    borderRadius: 6,
                  }}
                >
                  <input
                    value={s.title}
                    onChange={(e) =>
                      setSubtasks((xs) =>
                        xs.map((x) =>
                          x.id === s.id ? { ...x, title: e.target.value } : x,
                        ),
                      )
                    }
                    placeholder={`Subtask ${i + 1}…`}
                    style={inlineInput}
                  />
                  <button
                    type="button"
                    onClick={() => setSubtasks((xs) => xs.filter((x) => x.id !== s.id))}
                    style={iconBtn}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            style={ghostAdd}
            onClick={() => setSubtasks((s) => [...s, { id: cryptoId(), title: "" }])}
          >
            <Plus size={13} /> Add subtask
          </button>
        </FormSection>

        <FormSection>
          <FormLabel>Labels</FormLabel>
          <div style={labelRow}>
            {labels.map((l, i) => (
              <span key={i} style={labelPill}>
                {l}
                <PillRemoveBtn onClick={() => setLabels((ls) => ls.filter((_, idx) => idx !== i))} />
              </span>
            ))}
            <input
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const v = labelInput.trim();
                  if (v) {
                    setLabels((ls) => [...ls, v]);
                    setLabelInput("");
                  }
                }
              }}
              placeholder={labels.length === 0 ? "Type and press Enter…" : "Add…"}
              style={{
                flex: 1,
                minWidth: 120,
                height: 24,
                border: 0,
                background: "transparent",
                outline: 0,
                font: "400 12px/1 var(--bsp-font-sans)",
                color: "var(--bsp-fg)",
              }}
            />
          </div>
        </FormSection>
      </div>

      <Footer
        color={ENTITY.todo.color}
        primary="Create to-do"
        disabled={!canSubmit}
        onCancel={onClose}
        onSubmit={submit}
        error={submitError}
        summary={summary}
      />
    </>
  );
}

const scrollBody = {
  flex: 1,
  overflowY: "auto" as const,
  padding: "16px 22px 20px",
  display: "flex",
  flexDirection: "column" as const,
};

const privacyNote = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  padding: 10,
  background: DC.surfaceMuted,
  border: `1px solid ${DC.border}`,
  borderRadius: 6,
  fontSize: 12,
  color: DC.grey,
  lineHeight: 1.4,
};

const privateAssign = {
  padding: 10,
  background: DC.surfaceMuted,
  border: `1px solid ${DC.border}`,
  borderRadius: 6,
  fontSize: 12,
  color: DC.grey,
};

const teamNote = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 10px",
  background: DC.surfaceMuted,
  border: `1px solid ${DC.border}`,
  borderRadius: 6,
  fontSize: 11,
  color: DC.grey,
};

const labelRow = {
  display: "flex",
  flexWrap: "wrap" as const,
  alignItems: "center",
  gap: 6,
  padding: "8px 10px",
  border: `1px solid ${DC.borderStrong}`,
  borderRadius: 6,
  background: DC.surface,
  minHeight: 38,
};

const labelPill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 8px 4px 10px",
  background: DC.primaryTint,
  color: DC.primary,
  borderRadius: 999,
  font: "600 11px/1 var(--bsp-font-sans)",
};
